/** 
 * © 2025 COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 'CONTPAQi'. Todos los derechos reservados. 
 * Este código fuente constituye una obra original protegida por la legislación nacional e internacional en materia de propiedad intelectual. 
 * Su reproducción, distribución, comunicación pública, transformación, descompilación, ingeniería inversa o cualquier uso no autorizado, 
 * total o parcial, se encuentra expresamente prohibido sin la previa autorización por escrito de COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 
 * El uso indebido de este software puede ser constitutivo de delitos y dará lugar a las responsabilidades civiles y penales correspondientes. 
*/ 
 
import {put, takeEvery, select, call } from '@redux-saga/core/effects';
import { getUserLicensesSuccess, getUserLicensesFailure, getUserHasPermissionSuccess } from '../actions/user.action';
import { GET_USER_HAS_PERMISSION, GET_USER_LICENSES } from 'src/types/user.type';
import { ApiUsersUrl } from 'src/utils/config';
import { requestApi, parseResponse } from 'src/providers/http-api.provider';
import { getPrettierMessage } from 'src/utils/global';
import { Messages } from 'src/constants/message.constant';
import { ApiRbacUrl } from 'src/utils/config';
import { ApiRbacPaths } from 'src/constants/endpoints.constant';
import Role from 'src/models/role.model';
import { BaseAction } from 'src/types/global.type';

function* onGetUserLicenses(action:any):any {
  let errorMessage = '';
  try {
    const accessToken = yield select(state => state.config?.accessToken);
    if (accessToken ) {
      const endpoint = `${ApiUsersUrl}/GetUserLicenses`;
      let response = yield call(requestApi, {
        endpoint,
        method: 'GET',
        headers: {
          Authorization: accessToken,
        },
        useParseResponse: false,
      });
      if (response?.ok) {
        let licensesData = yield call(parseResponse, response);
        if (licensesData.length > 0) {
          yield put(getUserLicensesSuccess(licensesData));
        } else {
          yield put(getUserLicensesSuccess([]));
        }
      } else {
        const apiError = yield call(parseResponse, response);
        let apiErrorMessage = getPrettierMessage(apiError?.Message);
        if (apiErrorMessage) {
          errorMessage =`${apiErrorMessage} ${Messages.REINTENT_LABEL}`;
        }
        throw new Error(errorMessage);
      }
    } else {
      throw new Error("accessToken empty");
    }
  } catch (err) {
    console.log("Fail onGetUserLicenses: ", err);
    yield put(getUserLicensesFailure(err || Messages.ERROR_GENERIC));
  }
}

//TODO: Esta saga debería ser integrada con la api de agente de usuario
function* onGetUserPermissionsByApplication(data:BaseAction):any {
  let usersRolesByApplication:any = [];
  try {
    const platformToken = yield select(state => state.config?.platformToken);
    const identityId = yield select(state => state.config?.identityId);
    const licenseId = yield select(state => state.config?.licenseId);
    const permissionsSchema = yield select(state => state?.permissions);
    const services = data?.payload?.services;

    if (platformToken && services?.length > 0 && licenseId && identityId) {
      const endpoint = `${ApiRbacUrl}${ApiRbacPaths.BULK}/licenses/roles/users/${identityId}`;
      const payloadFilter = {
        licenseService: services.map((item:any) => {
          const service = {
            licenseId,
            serviceId: item.serviceId, 
          }
          return service;
        })
      };
      let response = yield call(requestApi, {
        endpoint,
        method: 'POST',
        headers: {
          Authorization: platformToken,
          'Content-Type': 'application/json',
        },
        body: payloadFilter,
        useParseResponse: false,
      });
      if (response?.ok) {
        let responseUserRoles = yield call(parseResponse, response);
        let responseData = responseUserRoles.results || [];
        if (responseData?.length > 0) {
          const endpointBulkRoles = `${ApiRbacUrl}${ApiRbacPaths.BULK}/licenses/roles`;
          let responseRoles = yield call(requestApi, {
            endpoint: endpointBulkRoles,
            method: 'POST',
            headers: {
              Authorization: platformToken,
              'Content-Type': 'application/json',
            },
            body: payloadFilter,
            useParseResponse: false,
          });
          if (responseRoles?.ok) {
            let responseFilterRoles = yield call(parseResponse, responseRoles);
            let responseDataRoles = responseFilterRoles.results || [];
            if (responseDataRoles && responseDataRoles?.length > 0) {
              responseData.forEach((filteredUser: any) => {
                const rolesIdsByUser = filteredUser?.users?.at(0)?.roleIds;
                const schema = permissionsSchema.applications.find((el:any)=>el.serviceId === filteredUser.serviceId)?.defaultSchema || null;
                const resourceId = schema?.resourceId;
                const actions = schema?.actions;
                let permissions = {
                  view: false,
                  update: false,
                  delete: false,
                  create: false,
                }
                let payloadUserRolesByApplication = {
                  serviceId: filteredUser.serviceId,
                  licenseId,
                  roles: permissions,
                };
                if (rolesIdsByUser?.length > 0) {
                  let filterRoles = responseDataRoles?.find(
                    (filteredRole: any) => filteredRole.serviceId === filteredUser.serviceId && 
                    filteredRole.licenseId === filteredUser.licenseId
                  );
                  if (filterRoles?.roles?.length > 0) {
                    let userRoles = filterRoles.roles.filter((role:any) => rolesIdsByUser.includes(role.roleId));
                    userRoles?.forEach((urol:any) => {
                      let resource = urol?.permissions?.find((el:any)=>el.resourceId?.toLowerCase() === resourceId?.toLowerCase());
                      if(resource?.actionIds?.length > 0) {
                        permissions['view'] = permissions?.view || resource?.actionIds?.includes(actions.view) || false;
                        permissions['update'] = permissions?.update || resource?.actionIds?.includes(actions.update) || false;
                        permissions['delete'] = permissions?.delete || resource?.actionIds?.includes(actions.delete) || false;
                        permissions['create'] = permissions?.create || resource?.actionIds?.includes(actions.create) || false;
                      }
                    });
                    payloadUserRolesByApplication['roles'] = permissions;
                  } 
                }
                usersRolesByApplication.push(payloadUserRolesByApplication);
              });
              yield put(getUserHasPermissionSuccess(usersRolesByApplication));
            } else {
              throw new Error(Messages.PERMISSIONS.GET_EMPTY_BY_USER);
            }
          } else {
            const apiError = yield call(parseResponse, responseRoles);
            throw new Error(getPrettierMessage(apiError?.detail));
          }
        } else {
          throw new Error(Messages.PERMISSIONS.GET_EMPTY_BY_USER);
        }
      } else {
        const apiError = yield call(parseResponse, response);
        throw new Error(getPrettierMessage(apiError?.detail));
      }
    } else {
      throw new Error(Messages.PARAMS_EMPTY);
    }
  } catch (err:any) {
    console.log("Fail onGetUserPermissionsByApplication: ", err);
    yield put(getUserHasPermissionSuccess([]));
  }
}

export function* watchOnGetUserLicenses() {
  yield takeEvery(GET_USER_LICENSES, onGetUserLicenses);
}

export function* watchOnGetUserPermissionsByApplication() {
  yield takeEvery(GET_USER_HAS_PERMISSION, onGetUserPermissionsByApplication);
}
